import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, Sparkles, Loader2, CheckCircle2, ImageIcon, Check, X } from "lucide-react";
import Btn from "../components/Btn";
import api, { apiErr } from "../lib/api";
import { QStimulus, OptMedia, hasMedia } from "../components/QMedia";

const EXAMS = { CPNS: ["TWK", "TIU", "TKP"], SNBT: ["PU", "PPU", "PBM", "PK"] };
const SUBTOPICS = {
  TWK: ["Kebangsaan", "Nasionalisme", "Bela Negara", "Pilar Negara"],
  TIU: ["Deret Angka", "Hitungan", "Logika Silogisme", "Analogi Verbal", "Kemampuan Figural"],
  TKP: ["Situasional", "Pelayanan Publik", "Jejaring Kerja"],
  PU: ["Penalaran Induktif", "Penalaran Deduktif", "Penalaran Kuantitatif", "Kemampuan Figural"],
  PPU: ["Kebahasaan", "Ide Pokok"],
  PBM: ["Pemahaman Bacaan"],
  PK: ["Kuantitatif", "Aljabar", "Aritmetika"],
};

export default function AdminGenerator() {
  const [form, setForm] = useState({ exam: "CPNS", category: "TIU", subcategory: "Deret Angka", count: 5, topic: "", figural_type: "mixed", difficulty: "hots" });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // {done,total}
  const [result, setResult] = useState([]);
  const [models, setModels] = useState(null);
  const [savingModel, setSavingModel] = useState(false);
  const pollRef = useRef(null);

  const isFigural = form.subcategory.toLowerCase().includes("figural");

  useEffect(() => {
    api.get("/admin/ai-models").then(({ data }) => setModels(data.models)).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, []);

  const saveModels = async () => {
    setSavingModel(true);
    try {
      await api.put("/admin/ai-models", models);
      toast.success("Model AI disimpan");
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingModel(false); }
  };

  const set = (k, v) => setForm((f) => {
    if (k === "exam") return { ...f, exam: v, category: EXAMS[v][0], subcategory: SUBTOPICS[EXAMS[v][0]][0] };
    if (k === "category") return { ...f, category: v, subcategory: (SUBTOPICS[v] || ["Umum"])[0] };
    return { ...f, [k]: v };
  });

  const poll = (jobId) => {
    const startedAt = Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - startedAt > 6 * 60 * 1000) {
        clearInterval(pollRef.current);
        setBusy(false); setProgress(null);
        toast.error("Pembuatan soal terlalu lama. Coba lagi dengan jumlah lebih kecil.");
        return;
      }
      try {
        const { data } = await api.get(`/admin/generate-jobs/${jobId}`);
        setProgress({ done: data.done, total: data.total });
        setResult(data.questions || []);
        if (data.status === "done" || data.status === "failed") {
          clearInterval(pollRef.current);
          setBusy(false);
          setProgress(null);
          if (data.status === "done") toast.success(`${data.created} soal dibuat — silakan kurasi di bawah`);
          else toast.error(data.error || "Gagal membuat soal");
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setBusy(false);
        setProgress(null);
        toast.error(apiErr(e));
      }
    }, 2500);
  };

  const generate = async () => {
    setBusy(true);
    setResult([]);
    setProgress({ done: 0, total: Number(form.count) });
    try {
      const { data } = await api.post("/admin/generate-questions", { ...form, count: Number(form.count) });
      poll(data.job_id);
    } catch (e) {
      toast.error(apiErr(e));
      setBusy(false);
      setProgress(null);
    }
  };

  const review = async (qids, action) => {
    try {
      const { data } = await api.post("/admin/questions/review", { qids, action });
      setResult((r) => r.filter((q) => !qids.includes(q.qid)));
      toast.success(action === "approve"
        ? `${data.affected} soal disetujui & masuk bank soal`
        : `${data.affected} soal ditolak`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      {models && (
        <div className="card-chunky p-5" data-testid="ai-model-config">
          <div className="flex items-center gap-2 mb-1"><Bot className="w-5 h-5 text-su-secondaryShadow" /><h3 className="font-heading font-black text-base">Model AI</h3></div>
          <p className="text-su-muted font-medium text-xs mb-4">Pilih model per jenis soal. <b>Flash</b> jauh lebih hemat token; kualitas gambar figural (SVG) tetap tajam.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <ModelSelect label="Soal Teks" testid="model-text" value={models.text} onChange={(v) => setModels((m) => ({ ...m, text: v }))} />
            <ModelSelect label="Soal Figural" testid="model-figural" value={models.figural} onChange={(v) => setModels((m) => ({ ...m, figural: v }))} />
          </div>
          <Btn data-testid="model-save" size="sm" variant="secondary" onClick={saveModels} disabled={savingModel} className="mt-3">
            {savingModel ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Model"}
          </Btn>
        </div>
      )}

      <div className="card-chunky p-6">
        <div className="flex items-center gap-2 mb-1"><Bot className="w-5 h-5 text-su-primary" /><h2 className="font-heading font-black text-xl">AI Question Generator</h2></div>
        <p className="text-su-muted font-medium text-sm mb-5">Gemini membuat soal baru & langsung masuk bank soal. Termasuk soal <b>figural (bergambar)</b> untuk SKD CPNS.</p>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <Select label="Ujian" testid="gen-exam" value={form.exam} onChange={(v) => set("exam", v)} options={Object.keys(EXAMS)} />
          <Select label="Kategori" testid="gen-category" value={form.category} onChange={(v) => set("category", v)} options={EXAMS[form.exam]} />
          <Field label="Subtopik" testid="gen-subcategory" value={form.subcategory} onChange={(v) => set("subcategory", v)} />
          <Field label="Jumlah (1-20)" testid="gen-count" type="number" value={form.count} onChange={(v) => set("count", v)} />
          <Select label="Tingkat Kesulitan" testid="gen-difficulty" value={form.difficulty} onChange={(v) => set("difficulty", v)}
            options={["hots", "hard", "medium", "mix"]}
            labels={{ hots: "HOTS / Tersulit (disarankan)", hard: "Sulit", medium: "Sedang", mix: "Campur (Mix)" }} />
        </div>

        <div className="mb-4 rounded-2xl border-2 border-su-energy/30 bg-su-energy/5 p-3 text-sm font-medium text-su-text" data-testid="gen-difficulty-note">
          Level <b>HOTS / Tersulit</b> membuat soal menuntut penalaran bertingkat & pengecoh menjebak — melatih kemampuan siswa lebih dalam.
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {(SUBTOPICS[form.category] || []).map((s) => (
            <button key={s} data-testid={`gen-subchip-${s}`} onClick={() => set("subcategory", s)}
              className={`px-3 py-1.5 rounded-xl font-heading font-extrabold text-xs border-2 transition-all inline-flex items-center gap-1 ${form.subcategory === s ? "bg-su-primary text-white border-su-primaryShadow" : "bg-su-surface text-su-muted border-su-border"}`}>
              {s.toLowerCase().includes("figural") && <ImageIcon className="w-3.5 h-3.5" />}{s}
            </button>
          ))}
        </div>

        <Field label="Topik spesifik (opsional)" testid="gen-topic" value={form.topic} onChange={(v) => set("topic", v)} placeholder="mis. barisan aritmetika bertingkat / pola rotasi" />

        {isFigural && (
          <>
            <div className="mt-3">
              <Select label="Jenis Soal Figural" testid="gen-figural-type" value={form.figural_type}
                onChange={(v) => set("figural_type", v)}
                options={["mixed", "analogi", "ketidaksamaan", "serial"]}
                labels={{ mixed: "Campur (acak 3 tipe)", analogi: "Analogi", ketidaksamaan: "Ketidaksamaan (beda sendiri)", serial: "Serial / Deret" }} />
            </div>
            <div className="mt-3 rounded-2xl border-2 border-su-primary/30 bg-su-primary/5 p-3 text-sm font-medium text-su-text flex items-start gap-2" data-testid="gen-figural-note">
              <ImageIcon className="w-5 h-5 text-su-primary shrink-0 mt-0.5" />
              <span>Mode figural HOTS: tiap soal berupa <b>gambar (SVG)</b> dengan 5 pilihan gambar. Pilih jenis: <b>Analogi</b>, <b>Ketidaksamaan</b> (beda sendiri), <b>Serial</b> (deret), atau <b>Campur</b>. Pembuatan lebih lama (~1 menit/soal), jadi mulai dari jumlah kecil ya.</span>
            </div>
          </>
        )}

        <Btn data-testid="gen-submit" onClick={generate} disabled={busy} className="w-full mt-4">
          {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> {progress ? `Membuat soal ${progress.done}/${progress.total}...` : "Membuat soal..."}</> : <><Sparkles className="w-5 h-5" /> Generate Soal</>}
        </Btn>
      </div>

      {result.length > 0 && (
        <div className="card-chunky p-6" data-testid="gen-results">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <h3 className="font-heading font-black text-lg flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-su-energy" /> {result.length} Soal Menunggu Kurasi</h3>
            <div className="flex gap-2">
              <Btn size="sm" onClick={() => review(result.map((q) => q.qid), "approve")} data-testid="gen-approve-all"><Check className="w-4 h-4" /> Setujui Semua</Btn>
              <Btn size="sm" variant="ghost" onClick={() => review(result.map((q) => q.qid), "reject")} data-testid="gen-reject-all"><X className="w-4 h-4" /> Tolak Semua</Btn>
            </div>
          </div>
          <p className="text-sm text-su-muted font-medium mb-4">Soal hasil AI <b>belum masuk bank soal</b>. Setujui yang bagus, tolak yang kurang.</p>
          <div className="space-y-4">
            {result.map((q, i) => (
              <div key={q.qid} className="rounded-2xl border-2 border-su-border p-4" data-testid={`gen-result-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <p className="font-heading font-extrabold text-sm">{i + 1}. {q.question_text}</p>
                  <span className={`shrink-0 text-[10px] font-heading font-black px-2 py-1 rounded-full ${DIFF_BADGE[q.difficulty] || DIFF_BADGE.medium}`}>{DIFF_LABEL[q.difficulty] || q.difficulty}</span>
                </div>
                <QStimulus q={q} />
                <div className="grid sm:grid-cols-2 gap-1.5 mb-2">
                  {q.options.map((o) => (
                    <div key={o.key} className={`text-sm font-medium px-2 py-1 rounded-lg flex items-center gap-2 ${o.key === q.correct_answer ? "bg-su-success/15 text-su-success" : "text-su-muted"}`}>
                      <b>{o.key}.</b> {hasMedia(o) ? <OptMedia opt={o} /> : o.text}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-su-muted font-medium mb-3"><b>Pembahasan:</b> {q.explanation}</p>
                <div className="flex gap-2">
                  <Btn size="sm" onClick={() => review([q.qid], "approve")} data-testid={`gen-approve-${i}`}><Check className="w-4 h-4" /> Setujui</Btn>
                  <Btn size="sm" variant="ghost" onClick={() => review([q.qid], "reject")} data-testid={`gen-reject-${i}`}><X className="w-4 h-4" /> Tolak</Btn>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const DIFF_LABEL = { hots: "HOTS", hard: "Sulit", medium: "Sedang", easy: "Mudah" };
const DIFF_BADGE = {
  hots: "bg-su-danger/15 text-su-danger", hard: "bg-su-energy/15 text-su-energy",
  medium: "bg-su-primary/15 text-su-primary", easy: "bg-su-success/15 text-su-success",
};

function Field({ label, testid, value, onChange, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-heading font-black uppercase text-su-muted">{label}</span>
      <input data-testid={testid} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium outline-none focus:border-su-primary" />
    </label>
  );
}

function Select({ label, testid, value, onChange, options, labels }) {
  return (
    <label className="block">
      <span className="text-xs font-heading font-black uppercase text-su-muted">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium outline-none focus:border-su-primary">
        {options.map((o) => <option key={o} value={o}>{labels?.[o] || o}</option>)}
      </select>
    </label>
  );
}

const MODEL_LABELS = {
  "gemini-3.1-pro-preview": "Gemini 3.1 Pro — kualitas terbaik",
  "gemini-3.5-flash": "Gemini 3.5 Flash — hemat & pintar",
  "gemini-3-flash-preview": "Gemini 3 Flash — paling hemat",
  "gemini-3.7-flash": "Gemini 3.7 Flash",
  "gemini-3.6-flash": "Gemini 3.6 Flash",
  "gemini-2.5-flash": "Gemini 2.5 Flash",
};
const MODEL_OPTS = Object.keys(MODEL_LABELS);

function ModelSelect({ label, testid, value, onChange }) {
  return (
    <label className="block">
      <span className="text-xs font-heading font-black uppercase text-su-muted">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium outline-none focus:border-su-primary">
        {MODEL_OPTS.map((o) => <option key={o} value={o}>{MODEL_LABELS[o]}</option>)}
      </select>
    </label>
  );
}
